/**
 * VendorsView (VENDOR-01) — the vendor directory, read from the ESC mirror.
 * Searchable list → slide-out detail (contacts, locations, recent POs, the
 * parts they supply with last cost). Foundation for Pegasus-native vendor + PO
 * management. Data loads from C:\ESCBACKUP\backfill-vendors-pos.ps1.
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  Search, Building2, Phone, Mail, Globe, MapPin, FileText, Package, ExternalLink, Truck,
  Pencil, Check, Plus,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { SlideOver } from "@/components/ui/SlideOver";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { SectionTitle } from "@/components/ui/PageHeader";
import { InfoTip } from "@/components/InfoTip";
import {
  useVendorList, useVendor, useVendorLocations, useVendorParts, useVendorPOs, useSaveVendorOverride,
  blankVendor, newVendorCode, type VendorRow, type VendorOverride,
} from "./vendors";

const money = (n: number | null) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s: string | null) =>
  !s ? "" : new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** Seed the edit draft from a vendor row (the editable overlay fields). */
function seedDraft(src: VendorRow): VendorOverride {
  return {
    name: src.name, phone: src.phone, phone2: src.phone2, email: src.email,
    web_url: src.web_url, terms: src.terms, account: src.account,
    discount: src.discount, tax_id: src.tax_id,
    address1: src.address1, city: src.city, state: src.state, zip: src.zip, notes: src.notes,
  };
}

export function VendorsView() {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const { data: vendors, isLoading } = useVendorList(search, showInactive);
  const [open, setOpen] = useState<VendorRow | null>(null);
  const [creating, setCreating] = useState<VendorRow | null>(null);

  const list = vendors ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader
        icon={Truck}
        title="Vendors"
        description="Who we buy from — contacts, terms, purchase history, and the parts each one supplies."
        actions={<Button onClick={() => setCreating(blankVendor(newVendorCode()))}><Plus className="h-4 w-4" /> New vendor</Button>}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor name, code, or city"
            className="w-full rounded-md border border-hairline py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" />
          Show inactive
        </label>
        <span className="text-xs text-slate-400">{list.length} shown</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Terms</th>
              <th className="px-3 py-2">Account #</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>
            )}
            {!isLoading && list.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-slate-400">
                No vendors loaded yet. Run <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">backfill-vendors-pos.ps1</code> on DC2022 to pull them from ESC.
              </td></tr>
            )}
            {list.map((v) => (
              <tr key={v.vendor_code} onClick={() => setOpen(v)}
                className={cn("cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50", v.inactive && "opacity-50")}>
                <td className="px-3 py-2 font-medium text-slate-800">
                  {v.name ?? v.vendor_code}
                  <span className="ml-1.5 text-[11px] text-slate-400">{v.vendor_code}</span>
                  {v.inactive && <span className="ml-1 text-[11px] text-slate-400">(inactive)</span>}
                </td>
                <td className="px-3 py-2 text-slate-500">{[v.city, v.state].filter(Boolean).join(", ")}</td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-500">{v.phone ?? "—"}</td>
                <td className="px-3 py-2 text-slate-500">{v.terms ?? "—"}</td>
                <td className="px-3 py-2 text-slate-500">{v.account ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <VendorDetail vendor={open} onClose={() => setOpen(null)} />
      {creating && <VendorDetail vendor={creating} isNew onClose={() => setCreating(null)} />}
    </div>
  );
}

function VendorDetail({ vendor, onClose, isNew }: { vendor: VendorRow | null; onClose: () => void; isNew?: boolean }) {
  const code = vendor?.vendor_code ?? null;
  const { data: locations } = useVendorLocations(isNew ? null : code);
  const { data: parts } = useVendorParts(isNew ? null : code);
  const { data: pos } = useVendorPOs(isNew ? null : code);
  const save = useSaveVendorOverride();
  // Re-read the freshest merged copy so a just-saved edit reflects in THIS open
  // panel (the `vendor` prop is the row captured at click-time and goes stale).
  const { data: fresh } = useVendor(isNew ? null : code);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<VendorOverride>({});

  useEffect(() => {
    if (!vendor) return;
    setEditing(!!isNew);
    setDraft(seedDraft(vendor));
  }, [vendor, isNew]);

  if (!vendor) return <SlideOver open={false} onClose={onClose}>{null}</SlideOver>;
  const v = fresh ?? vendor;

  const set = (k: keyof VendorOverride, val: string) => setDraft((d) => ({ ...d, [k]: val || null }));
  const startEdit = () => { setDraft(seedDraft(v)); setEditing(true); };
  const onSave = () => save.mutate({ vendor_code: vendor.vendor_code, ...draft }, {
    onSuccess: () => { if (isNew) onClose(); else setEditing(false); },
  });
  const addr = [v.address1, v.address2, [v.city, v.state, v.zip].filter(Boolean).join(" ")].filter(Boolean);
  const titleText = isNew ? "New vendor" : (v.name ?? v.vendor_code);

  const footer = editing ? (
    <div className="flex items-center justify-end gap-2">
      <Button variant="secondary" size="sm" onClick={() => (isNew ? onClose() : setEditing(false))}>Cancel</Button>
      <Button size="sm" onClick={onSave} disabled={save.isPending || (isNew && !(draft.name ?? "").trim())}>
        <Check className="h-3.5 w-3.5" /> {save.isPending ? "Saving…" : isNew ? "Add vendor" : "Save"}
      </Button>
    </div>
  ) : undefined;

  return (
    <SlideOver open={!!vendor} onClose={onClose} size="lg" footer={footer}
      title={editing ? (
        <span className="inline-flex max-w-full items-center gap-1.5">
          <span className="min-w-0 truncate">{titleText}</span>
          <InfoTip label="Where edits save">Saved in Pegasus. ESC stays untouched.</InfoTip>
        </span>
      ) : titleText}
      subtitle={isNew ? undefined : `Vendor ${v.vendor_code}${v.inactive ? " · inactive" : ""}`}>
      <div className="space-y-5">
        {!editing ? (
          <section className="space-y-2 text-sm">
            <div className="-mt-1 flex justify-end">
              <Button variant="ghost" size="sm" onClick={startEdit}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
            {addr.length > 0 && (
              <div className="flex items-start gap-2 text-slate-600">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>{addr.join(", ")}</span>
              </div>
            )}
            {v.phone && <Row icon={Phone}>{v.phone}{v.phone2 ? ` · ${v.phone2}` : ""}</Row>}
            {v.email && <Row icon={Mail}><a className="text-brand-700 hover:underline" href={`mailto:${v.email}`}>{v.email}</a></Row>}
            {v.web_url && (
              <Row icon={Globe}>
                <a className="inline-flex items-center gap-1 text-brand-700 hover:underline" href={withHttp(v.web_url)} target="_blank" rel="noreferrer">
                  {v.web_url} <ExternalLink className="h-3 w-3" />
                </a>
              </Row>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-slate-500">
              {v.terms && <span>Terms: <strong className="text-slate-700">{v.terms}</strong></span>}
              {v.account && <span>Account #: <strong className="text-slate-700">{v.account}</strong></span>}
              {v.discount ? <span>Discount: <strong className="text-slate-700">{v.discount}%</strong></span> : null}
              {v.tax_id && <span>Tax ID: <strong className="text-slate-700">{v.tax_id}</strong></span>}
            </div>
            {v.notes && (
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {v.notes}
              </div>
            )}
          </section>
        ) : (
          <section className="space-y-3">
            <VField label="Vendor name"><input value={draft.name ?? ""} onChange={(e) => set("name", e.target.value)} className="vinp" /></VField>
            <div className="grid grid-cols-2 gap-3">
              <VField label="Phone"><input value={draft.phone ?? ""} onChange={(e) => set("phone", e.target.value)} className="vinp" /></VField>
              <VField label="Alt phone"><input value={draft.phone2 ?? ""} onChange={(e) => set("phone2", e.target.value)} className="vinp" /></VField>
              <VField label="Email"><input value={draft.email ?? ""} onChange={(e) => set("email", e.target.value)} className="vinp" /></VField>
              <VField label="Website"><input value={draft.web_url ?? ""} onChange={(e) => set("web_url", e.target.value)} className="vinp" /></VField>
              <VField label="Terms"><input value={draft.terms ?? ""} onChange={(e) => set("terms", e.target.value)} className="vinp" /></VField>
              <VField label="Account #"><input value={draft.account ?? ""} onChange={(e) => set("account", e.target.value)} className="vinp" /></VField>
              <VField label="Discount (%)"><input type="number" step="0.1" min="0" value={draft.discount ?? ""} onChange={(e) => setDraft((d) => ({ ...d, discount: e.target.value === "" ? null : Number(e.target.value) }))} className="vinp" /></VField>
              <VField label="Tax ID (1099)"><input value={draft.tax_id ?? ""} onChange={(e) => set("tax_id", e.target.value)} className="vinp" /></VField>
            </div>
            <VField label="Address"><input value={draft.address1 ?? ""} onChange={(e) => set("address1", e.target.value)} className="vinp" /></VField>
            <div className="grid grid-cols-3 gap-3">
              <VField label="City"><input value={draft.city ?? ""} onChange={(e) => set("city", e.target.value)} className="vinp" /></VField>
              <VField label="State"><input value={draft.state ?? ""} onChange={(e) => set("state", e.target.value)} className="vinp" /></VField>
              <VField label="ZIP"><input value={draft.zip ?? ""} onChange={(e) => set("zip", e.target.value)} className="vinp" /></VField>
            </div>
            <VField label="Notes"><textarea rows={2} value={draft.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className="vinp resize-none" /></VField>
            <style>{`.vinp{width:100%;border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.4rem 0.55rem;font-size:0.875rem}.vinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
          </section>
        )}

        {/* Locations + contacts */}
        {locations && locations.length > 0 && (
          <Block title={`Locations & contacts (${locations.length})`} icon={Building2}>
            <div className="space-y-2">
              {locations.map((l) => (
                <div key={l.list_id} className="rounded-lg border border-hairline p-2.5 text-xs">
                  <div className="font-medium text-slate-700">{l.loc_name ?? "Location"}</div>
                  <div className="text-slate-500">{[l.address1, l.city, l.state, l.zip].filter(Boolean).join(", ")}</div>
                  {(l.contacts ?? []).filter((c) => c.name || c.email || c.phone).map((c, i) => (
                    <div key={i} className="mt-1 flex flex-wrap gap-x-3 text-slate-600">
                      {c.name && <span className="font-medium">{c.name}</span>}
                      {c.phone && <span>{c.phone}{c.ext ? ` x${c.ext}` : ""}</span>}
                      {c.email && <a className="text-brand-700 hover:underline" href={`mailto:${c.email}`}>{c.email}</a>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Block>
        )}

        {/* Parts supplied */}
        {parts && parts.length > 0 && (
          <Block title={`Parts supplied (${parts.length})`} icon={Package}>
            <div className="overflow-hidden rounded-lg border border-hairline">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="px-2 py-1.5">Part</th>
                    <th className="px-2 py-1.5">Vendor part #</th>
                    <th className="px-2 py-1.5 text-right">Last cost</th>
                    <th className="px-2 py-1.5 text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.slice(0, 100).map((p) => (
                    <tr key={p.part} className="border-t border-hairline">
                      <td className="px-2 py-1.5 font-medium text-slate-700">{p.part}</td>
                      <td className="px-2 py-1.5 text-slate-500">{p.ven_part ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{money(p.last_purchase_price)}</td>
                      <td className="px-2 py-1.5 text-right text-slate-400">{fmtDate(p.date_last_purchase)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Block>
        )}

        {/* Recent POs */}
        {pos && pos.length > 0 && (
          <Block title={`Recent purchase orders (${pos.length})`} icon={FileText}>
            <div className="space-y-1">
              {pos.map((p) => (
                <div key={p.po} className="flex items-center justify-between gap-2 rounded-lg border border-hairline px-2.5 py-1.5 text-xs">
                  <span className="font-medium text-slate-700">PO {p.po.replace(/^0+/, "")}</span>
                  <span className="text-slate-500">{fmtDate(p.date)}</span>
                  <span className="text-slate-400">{p.billed ? "billed" : p.order_placed ? "ordered" : "open"}</span>
                </div>
              ))}
            </div>
          </Block>
        )}
      </div>
    </SlideOver>
  );
}

function Row({ icon: Icon, children }: { icon: typeof Phone; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-slate-600">
      <Icon className="h-4 w-4 shrink-0 text-slate-400" /> {children}
    </div>
  );
}

function Block({ title, icon: Icon, children }: { title: string; icon: typeof Phone; children: ReactNode }) {
  return (
    <section>
      <SectionTitle icon={Icon} title={title} className="mb-2" />
      {children}
    </section>
  );
}

function VField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const withHttp = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
